"use client";

import { ArrowRight, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PricingQuestions() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    numeral: "01",
    question: "How do you determine your negotiation fees?",
    answer: "Our fees are transparent and based on the complexity of the purchase. We typically operate on a flat-fee structure or a percentage of the total savings we secure for you, ensuring our incentives are perfectly aligned with your financial goals."
  }, {
    numeral: "02",
    question: "What types of purchases do you handle?",
    answer: "We specialize in high-value consumer purchases, including luxury vehicles, home appliances, furniture packages, and complex service contracts. If it involves a significant investment, we have the expertise to advocate for your best price."
  }, {
    numeral: "03",
    question: "Do I need to be present during negotiations?",
    answer: "Not at all. Once we have established your requirements and budget, our team handles all communications with vendors. We provide you with regular updates and present you with the final, optimized offer for your approval."
  }, {
    numeral: "04",
    question: "What if you cannot secure a lower price?",
    answer: "If we are unable to improve upon the market price, we will provide a detailed report on why the current offer is competitive. In such cases, we often pivot to negotiating better terms, extended warranties, or added value inclusions."
  }, {
    numeral: "05",
    question: "How do I get started with a consultation?",
    answer: "Getting started is simple. You can book a complimentary discovery call through our website. During this call, we will review your upcoming purchase plans and determine how our advocacy services can best support your needs."
  }, {
    numeral: "06",
    question: "Are your services confidential?",
    answer: "Absolutely. We maintain strict confidentiality regarding all client information and purchase details. Your privacy is a cornerstone of our professional advocacy practice."
  }, {
    numeral: "07",
    question: "Can you help with business-to-business purchases?",
    answer: "While our primary focus is consumer advocacy, we do assist small business owners with procurement negotiations for office equipment and service contracts. Please contact us to discuss your specific business requirements."
  }, {
    numeral: "08",
    question: "Do you work with specific brands?",
    answer: "We are brand-agnostic. Our loyalty is exclusively to you, the consumer. We leverage our market knowledge to negotiate with any vendor, regardless of the brand, to ensure you receive the best possible value."
  }, {
    numeral: "09",
    question: "What is the typical turnaround time?",
    answer: "Negotiation timelines vary based on the vendor's responsiveness and the complexity of the deal. Most negotiations are concluded within 3 to 7 business days, allowing you to move forward with your purchase confidently."
  }, {
    numeral: "10",
    question: "Is there a guarantee on savings?",
    answer: "While we cannot guarantee a specific dollar amount, we guarantee our commitment to exhausting every negotiation lever available. Our track record demonstrates significant savings for the vast majority of our clients."
  }];
  return <section data-section-id="650" id="pricing-questions" className="py-24 px-6 md:px-10 lg:px-16 bg-background text-foreground">
      <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {}
        <div className="lg:col-span-5">
          <div ref={leftRef} className={`lg:sticky lg:top-32 transition-all duration-800 ease-[cubic-bezier(0.16,1,0.3,1)] ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[30px]"}`}>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-3 h-3 bg-[#e87a27]" />
              <p className="font-default text-xs font-bold uppercase tracking-[0.2em]" style={{
              color: "#e87a27"
            }}>
                Pricing — FAQ
              </p>
            </div>
            <h2 className="font-default font-bold uppercase leading-[1.2] text-[#1a3a6b] mb-8" style={{
            fontSize: "clamp(2rem, 4vw, 3rem)"
          }}>
              Common Questions
              <br />
              About Our Fees
            </h2>
            <p className="font-default text-lg leading-relaxed text-[#333333] mb-10 max-w-md">
              Transparency is the foundation of our advocacy. We have compiled the most frequent questions regarding our pricing and negotiation process to help you feel confident in your investment.
            </p>
            <Link to="/AboutContactPage" className="group inline-flex items-center gap-2 text-base font-default font-semibold text-[#1a3a6b] hover:opacity-80 transition-opacity">
              <span className="relative">
                Still have questions? Contact us
                <span className="absolute left-0 -bottom-0.5 h-px w-full origin-left scale-x-100 transition-transform duration-500 group-hover:scale-x-0 bg-[#1a3a6b]" />
              </span>
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {}
        <div className="lg:col-span-7">
          <div ref={rightRef} className={`transition-all duration-800 ease-[cubic-bezier(0.16,1,0.3,1)] ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[30px]"}`}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-b border-[#cccccc] last:border-b border-t-0">
                  <AccordionTrigger className="group py-7 hover:no-underline [&>svg]:hidden gap-6 items-center">
                    <div className="flex items-center gap-6 md:gap-10 flex-1 text-left">
                      <span className="font-default text-2xl font-bold shrink-0 w-12" style={{
                    color: "#1a3a6b"
                  }}>
                        {faq.numeral}
                      </span>
                      <span className="font-default text-xl font-semibold text-[#333333] leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="w-6 h-6 shrink-0 transition-transform duration-400 ease-out group-data-[state=open]:rotate-45" style={{
                  color: "#e87a27"
                }} />
                  </AccordionTrigger>
                  <AccordionContent className="pb-7">
                    <div className="pl-22 md:pl-26 pr-8 pt-2">
                      <p className="font-default text-base text-[#333333]" style={{
                    lineHeight: "1.6"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}